import React, { useState, useMemo, useRef, useEffect } from 'react';
import { X, Users, Building, Package, ChevronDown, Search } from 'lucide-react';
import axios from 'axios';

const InspectionNotification = ({ isOpen, onClose, equipmentIds, companies }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [equipmentList, setEquipmentList] = useState([]);
  const [notificationSelections, setNotificationSelections] = useState({});
  const [inspectorContactSelections, setInspectorContactSelections] = useState({});
  const [selectedInspectors, setSelectedInspectors] = useState({});
  const [companyInspectors, setCompanyInspectors] = useState([]);
  const [minimalUsers, setMinimalUsers] = useState([]);  // Minimal user data with emails
  const [inspectorSearchTerms, setInspectorSearchTerms] = useState({});
  const [inspectorDropdownOpen, setInspectorDropdownOpen] = useState({});
  const dropdownRefs = useRef({});
  
  // Helper functions to calculate validity dates and status
  const calculateManufacturerExpiry = (equipment) => {
    // Use cached manufacturer_validity_date from backend (already calculated)
    if (!equipment.manufacturer_validity_date) {
      return null;
    }

    return new Date(equipment.manufacturer_validity_date);
  };
  
  const calculateInspectionExpiry = (equipment) => {
    // Check if equipment has recent valid inspections
    if (equipment.latest_inspection && equipment.latest_inspection.validity_date) {
      return new Date(equipment.latest_inspection.validity_date);
    }
    
    // Fallback to legacy field
    if (equipment.safety_equipment_date_of_validity) {
      return new Date(equipment.safety_equipment_date_of_validity);
    }
    
    return null;
  };
  
  const getManufacturerValidityStatus = (equipment) => {
    const expiryDate = calculateManufacturerExpiry(equipment);
    if (!expiryDate) return 'unknown';
    
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) return 'expired';
    if (daysUntilExpiry <= 30) return 'expiring';
    return 'valid';
  };
  
  const getInspectionValidityStatus = (equipment) => {
    const expiryDate = calculateInspectionExpiry(equipment);
    if (!expiryDate) return 'expiring'; // Treat items with no inspection date as expiring (< 30 days)
    
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) return 'expired';
    if (daysUntilExpiry <= 30) return 'expiring';
    return 'valid';
  };
  
  const getManufacturerValidityDisplay = (equipment) => {
    const expiryDate = calculateManufacturerExpiry(equipment);
    if (!expiryDate) return 'Not set';
    
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return `Expired ${Math.abs(daysUntilExpiry)} days ago`;
    } else if (daysUntilExpiry === 0) {
      return 'Expires today';
    } else if (daysUntilExpiry <= 30) {
      return `Expires in ${daysUntilExpiry} days`;
    } else {
      return expiryDate.toLocaleDateString();
    }
  };
  
  const getInspectionValidityDisplay = (equipment) => {
    const expiryDate = calculateInspectionExpiry(equipment);
    if (!expiryDate) return 'Not set';
    
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return `Expired ${Math.abs(daysUntilExpiry)} days ago`;
    } else if (daysUntilExpiry === 0) {
      return 'Expires today';
    } else if (daysUntilExpiry <= 30) {
      return `Expires in ${daysUntilExpiry} days`;
    } else {
      return expiryDate.toLocaleDateString();
    }
  };
  
  // Fetch full equipment data from API when equipmentIds change
  useEffect(() => {
    const fetchEquipmentData = async () => {
      if (!isOpen || !equipmentIds || equipmentIds.length === 0) {
        setEquipmentList([]);
        return;
      }

      try {
        setIsLoading(true);
        const token = localStorage.getItem('access_token');
        const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

        // Fetch full data for each equipment ID from the old endpoint
        const promises = equipmentIds.map(id =>
          axios.get(`${API_BASE_URL}/safetyequipment/${id}/`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        );

        const responses = await Promise.all(promises);
        const fullEquipmentData = responses.map(response => response.data);

        setEquipmentList(fullEquipmentData);
      } catch (error) {
        console.error('Error fetching equipment data:', error);
        setEquipmentList([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEquipmentData();
  }, [isOpen, equipmentIds]);

  // Initialize notification selections when equipment list changes
  useEffect(() => {
    if (equipmentList && equipmentList.length > 0) {
      const initialSelections = {};
      equipmentList.forEach(equipment => {
        // Default: select inspection if expiring/expired, manufacturer if expiring/expired
        const inspectionStatus = getInspectionValidityStatus(equipment);
        const manufacturerStatus = getManufacturerValidityStatus(equipment);

        initialSelections[equipment.id] = {
          inspection: inspectionStatus === 'expired' || inspectionStatus === 'expiring',
          manufacturer: manufacturerStatus === 'expired' || manufacturerStatus === 'expiring'
        };
      });
      setNotificationSelections(initialSelections);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentList]);
  
  // Fetch users data when component opens (for inspectors and email lookup)
  React.useEffect(() => {
    const fetchUsersData = async () => {
      if (!isOpen) return;

      try {
        const token = localStorage.getItem('access_token');
        const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

        // Single request - /users/ contains all needed data (inspector flag + company_email)
        const usersResponse = await axios.get(`${API_BASE_URL}/users/`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Filter users who are inspectors in the current company
        const inspectors = usersResponse.data.filter(user =>
          user.inspector === true && user.is_active === true
        );

        setCompanyInspectors(inspectors);
        setMinimalUsers(usersResponse.data); // Same data used for email lookup
      } catch (error) {
        console.error('Error fetching users data:', error);
      }
    };

    fetchUsersData();
  }, [isOpen]);
  
  // Handle click outside to close dropdowns
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(dropdownRefs.current).forEach(groupKey => {
        if (dropdownRefs.current[groupKey] && !dropdownRefs.current[groupKey].contains(event.target)) {
          setInspectorDropdownOpen(prev => ({
            ...prev,
            [groupKey]: false
          }));
        }
      });
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);
  
  // Handler for checkbox changes
  const handleNotificationTypeChange = (equipmentId, notificationType) => {
    setNotificationSelections(prev => ({
      ...prev,
      [equipmentId]: {
        ...prev[equipmentId],
        [notificationType]: !prev[equipmentId]?.[notificationType]
      }
    }));
  };
  
  // Reset all inspector contact selections
  const resetInspectorSelections = () => {
    setInspectorContactSelections({});
    setSelectedInspectors({});
    setInspectorSearchTerms({});
    setInspectorDropdownOpen({});
  };
  
  // Handle cancel button click
  const handleCancel = () => {
    resetInspectorSelections();
    onClose();
  };
  
  // Check if group has any equipment with inspection notification selected
  const hasInspectionNotifications = (group) => {
    return group.equipment.some(eq => notificationSelections[eq.id]?.inspection);
  };
  
  // Handler for inspector contact checkbox
  const handleInspectorContactChange = (groupKey) => {
    setInspectorContactSelections(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
    
    // Clear selected inspectors if unchecking
    if (inspectorContactSelections[groupKey]) {
      setSelectedInspectors(prev => ({
        ...prev,
        [groupKey]: []
      }));
      setInspectorSearchTerms(prev => ({
        ...prev,
        [groupKey]: ''
      }));
      setInspectorDropdownOpen(prev => ({
        ...prev,
        [groupKey]: false
      }));
    }
  };
  
  // Handler for inspector selection (multi-select)
  const handleInspectorToggle = (groupKey, inspectorId) => {
    setSelectedInspectors(prev => {
      const currentSelection = prev[groupKey] || [];
      const isSelected = currentSelection.includes(inspectorId);
      
      return {
        ...prev,
        [groupKey]: isSelected 
          ? currentSelection.filter(id => id !== inspectorId)
          : [...currentSelection, inspectorId]
      };
    });
  };
  
  // Handler for search term changes
  const handleInspectorSearch = (groupKey, searchTerm) => {
    setInspectorSearchTerms(prev => ({
      ...prev,
      [groupKey]: searchTerm
    }));
  };
  
  // Handler for dropdown toggle
  const toggleInspectorDropdown = (groupKey) => {
    setInspectorDropdownOpen(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };
  
  // Filter inspectors based on search term
  const getFilteredInspectors = (groupKey) => {
    const searchTerm = inspectorSearchTerms[groupKey] || '';
    if (!searchTerm.trim()) return companyInspectors;
    
    return companyInspectors.filter(inspector => {
      const fullName = `${inspector.first_name || ''} ${inspector.last_name || ''}`.trim().toLowerCase();
      const username = inspector.username.toLowerCase();
      const email = (inspector.email || '').toLowerCase();
      const search = searchTerm.toLowerCase();
      
      return fullName.includes(search) || username.includes(search) || email.includes(search);
    });
  };
  
  // Group equipment by recipients - must be called before any early returns
  const groupedNotifications = useMemo(() => {
    // Return empty array if component shouldn't render
    if (!isOpen || !equipmentList || equipmentList.length === 0) return [];
    
    const groups = new Map();
    
    // Get current user company NAME from localStorage (logged in user)
    // Note: localStorage stores company as NAME (string), not ID
    const getCurrentUserCompanyName = () => {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      // userData.company is company NAME (string), not ID
      // Also check company_name field in case it exists
      return userData.company_name || userData.company || null;
    };

    const currentUserCompanyName = getCurrentUserCompanyName();
    console.log('🔍 DEBUG currentUserCompanyName:', currentUserCompanyName);

    equipmentList.forEach((equipment, index) => {
      const equipmentCompany = companies?.find(c => c.id === equipment.company);
      const equipmentCompanyName = equipment.company_name || equipmentCompany?.name;
      let recipientKey = '';
      let recipientInfo = null;

      console.log(`\n📦 DEBUG Equipment #${index}:`, {
        id: equipment.id,
        status: equipment.safety_equipment_status,
        'equipment.company (ID)': equipment.company,
        'equipment.company_name': equipmentCompanyName,
        'currentUserCompanyName': currentUserCompanyName,
        'are company names equal?': equipmentCompanyName === currentUserCompanyName,
        'assignment_data': equipment.assignment_data,
        'assigned_to': equipment.assigned_to,
        'assigned_to_name': equipment.assigned_to_name
      });

      // Check if equipment is private (shared with our company)
      if (equipment.safety_equipment_status === 'private') {
        console.log('  ➡️ MATCHED: private status');
        // Private equipment - send to personal owner
        // Note: backend returns personal_owner (ID) and personal_owner_id depending on endpoint
        const ownerId = equipment.personal_owner_id || equipment.personal_owner;
        if (ownerId) {
          recipientKey = `user_${ownerId}`;
          recipientInfo = {
            type: 'user',
            name: equipment.personal_owner_name || 'Unknown Owner',
            email: equipment.personal_owner_email,
            company: 'Private Equipment',
            userId: ownerId
          };
        }
      } else if (currentUserCompanyName && equipmentCompanyName && equipmentCompanyName !== currentUserCompanyName) {
        console.log('  ➡️ MATCHED: external company (equipmentCompanyName !== currentUserCompanyName)');
        // External company - send to company email
        recipientKey = `company_${equipment.company}`;
        recipientInfo = {
          type: 'company',
          name: equipmentCompanyName || 'Unknown Company',
          email: equipmentCompany?.ppe_company_email || equipmentCompany?.company_email,
          company: equipmentCompanyName || 'Unknown Company'
        };
      } else if (['issued', 'received'].includes(equipment.safety_equipment_status)) {
        console.log('  ➡️ MATCHED: same company + issued/received');
        // Same company but issued/received - find assigned user
        // Use assigned_to (user ID) and assigned_to_name directly from equipment object
        // or fall back to assignment_data if available
        const assignedUserId = equipment.assigned_to || equipment.assignment_data?.user_id;
        const assignedUserName = equipment.assigned_to_name || equipment.assignment_data?.user_name;

        console.log('  📋 assignedUserId:', assignedUserId, 'assignedUserName:', assignedUserName);

        if (assignedUserId) {
          const assignedUser = minimalUsers?.find(u => u.id === assignedUserId);
          console.log('  👤 assignedUser found:', assignedUser);
          if (assignedUser) {
            recipientKey = `user_${assignedUser.id}`;
            const userName = assignedUserName || `${assignedUser.first_name || ''} ${assignedUser.last_name || ''}`.trim() || assignedUser.username;
            recipientInfo = {
              type: 'user',
              name: userName,
              email: assignedUser.company_email,
              company: equipmentCompanyName || 'Unknown Company',
              userId: assignedUser.id
            };
          }
        } else {
          console.log('  ⚠️ No assigned_to user ID found');
        }
      } else {
        console.log('  ⚠️ NO CONDITION MATCHED - status:', equipment.safety_equipment_status);
      }

      console.log('  📧 recipientInfo:', recipientInfo);
      
      // Always create group, even if no email (for warning display)
      if (recipientInfo) {
        if (!groups.has(recipientKey)) {
          groups.set(recipientKey, {
            recipient: recipientInfo,
            equipment: [],
            hasValidEmail: !!(recipientInfo.email && recipientInfo.email.trim())
          });
        }
        groups.get(recipientKey).equipment.push(equipment);
      } else {
        // Create a group for items without identifiable recipients
        const noRecipientKey = `no_recipient_${equipment.id}`;
        const companyName = equipment.safety_equipment_status === 'private'
          ? 'Private Equipment'
          : (companies?.find(c => c.id === equipment.company)?.name || 'Unknown Company');
        groups.set(noRecipientKey, {
          recipient: {
            type: 'unknown',
            name: 'No recipient found',
            email: '',
            company: companyName
          },
          equipment: [equipment],
          hasValidEmail: false
        });
      }
    });
    
    return Array.from(groups.values());
  }, [equipmentList, companies, minimalUsers]);
  
  // Early return after all hooks
  if (!isOpen || !equipmentList || equipmentList.length === 0) return null;
  
  const handleSendNotifications = async () => {
    // Filter groups with valid emails
    const validGroups = groupedNotifications.filter(group => group.hasValidEmail);
    
    if (validGroups.length === 0) {
      alert('No valid email addresses found for sending notifications.');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy'}/api`;
      const token = localStorage.getItem('access_token');
      
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      
      // Send notification for each valid recipient group
      for (const group of validGroups) {
        try {
          // Build payload with notification type selections for each equipment
          const equipmentNotifications = group.equipment.map(eq => ({
            equipment_id: eq.id,
            notify_manufacturer: notificationSelections[eq.id]?.manufacturer || false,
            notify_inspection: notificationSelections[eq.id]?.inspection || false
          }));
          
          // Get inspector contact info for this group
          const groupKey = `${group.recipient.type}_${group.recipient.userId || group.equipment[0]?.company || group.recipient.email}`;
          const selectedInspectorIds = selectedInspectors[groupKey] || [];
          const inspectorContacts = inspectorContactSelections[groupKey] && selectedInspectorIds.length > 0 
            ? selectedInspectorIds 
            : [];

          const payload = {
            equipment_ids: group.equipment.map(eq => eq.id),
            equipment_notifications: equipmentNotifications,
            recipient_email: group.recipient.email,
            recipient_type: group.recipient.type,
            recipient_name: group.recipient.name,
            inspector_contact_ids: inspectorContacts
          };
          
          const response = await axios.post(
            `${API_BASE_URL}/send-inspection-notifications/`,
            payload,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          if (response.status === 200) {
            successCount++;
          }
        } catch (error) {
          console.error(`Error sending notification to ${group.recipient.email}:`, error);
          errorCount++;
          errors.push(`${group.recipient.name}: ${error.response?.data?.error || error.message}`);
        }
      }
      
      // Show result
      if (successCount > 0 && errorCount === 0) {
        alert(`Successfully sent ${successCount} inspection notification${successCount > 1 ? 's' : ''}!`);
        resetInspectorSelections();
        onClose();
      } else if (successCount > 0 && errorCount > 0) {
        alert(`Sent ${successCount} notifications successfully, but ${errorCount} failed:\n${errors.join('\n')}`);
      } else {
        alert(`Failed to send all notifications:\n${errors.join('\n')}`);
      }
      
    } catch (error) {
      console.error('Error sending inspection notifications:', error);
      alert('Failed to send inspection notifications. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white rounded-t-xl">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Inspection Notifications ({equipmentList.length} items, {groupedNotifications.length} recipients)
            </h2>
            {(() => {
              const validGroups = groupedNotifications.filter(g => g.hasValidEmail);
              const invalidGroups = groupedNotifications.filter(g => !g.hasValidEmail);
              const invalidItemsCount = invalidGroups.reduce((sum, g) => sum + g.equipment.length, 0);
              
              if (invalidItemsCount > 0) {
                return (
                  <p className="text-sm text-orange-600 mt-1">
                    ⚠️ {validGroups.length} will be sent, {invalidItemsCount} items cannot be sent (missing emails)
                  </p>
                );
              }
            })()}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {groupedNotifications.length > 0 ? (
            <div className="space-y-6">
              {/* Valid groups (with emails) */}
              {groupedNotifications.filter(group => group.hasValidEmail).map((group, index) => (
                <div key={`valid-${index}`} className="border border-green-200 bg-green-50 rounded-lg p-4">
                  {/* Recipient Info */}
                  <div className="flex items-start space-x-3 mb-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        {group.recipient.type === 'user' ? (
                          <Users className="h-5 w-5 text-green-600" />
                        ) : group.recipient.type === 'company' ? (
                          <Building className="h-5 w-5 text-green-600" />
                        ) : (
                          <Package className="h-5 w-5 text-green-600" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-medium text-gray-900">
                          {group.recipient.name}
                        </h3>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          ✓ Will be sent
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        {group.recipient.company}
                      </p>
                      <p className="text-sm text-green-600 font-medium">
                        {group.recipient.email}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <Package className="h-4 w-4" />
                      <span>{group.equipment.length} item{group.equipment.length > 1 ? 's' : ''}</span>
                    </div>
                  </div>

                  {/* Inspector Contact Section - Only for internal recipients */}
                  {(() => {
                    // Get current user company NAME to determine if this is an internal recipient
                    const getCurrentUserCompanyName = () => {
                      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
                      return userData.company_name || userData.company || null;
                    };

                    const currentUserCompanyName = getCurrentUserCompanyName();
                    const equipmentCompanyName = group.equipment[0]?.company_name;
                    const isInternalRecipient = group.recipient.type === 'user' ||
                      (group.recipient.type === 'company' && equipmentCompanyName === currentUserCompanyName);
                    
                    // Only show for internal recipients, if we have inspectors, and if group has inspection notifications
                    if (!isInternalRecipient || companyInspectors.length === 0 || !hasInspectionNotifications(group)) {
                      return null;
                    }
                    
                    const groupKey = `${group.recipient.type}_${group.recipient.userId || group.equipment[0]?.company || index}`;
                    
                    return (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                        <div className="flex items-center space-x-2 mb-2">
                          <input
                            type="checkbox"
                            id={`inspector-contact-${groupKey}`}
                            checked={inspectorContactSelections[groupKey] || false}
                            onChange={() => handleInspectorContactChange(groupKey)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label 
                            htmlFor={`inspector-contact-${groupKey}`}
                            className="text-sm font-medium text-gray-900 cursor-pointer"
                          >
                            Contact inspector
                          </label>
                        </div>
                        
                        {inspectorContactSelections[groupKey] && (
                          <div className="mt-2 relative">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Select inspectors:
                            </label>
                            
                            {/* Selected Inspectors Display */}
                            <div className="mb-2">
                              {(selectedInspectors[groupKey] || []).length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {(selectedInspectors[groupKey] || []).map(inspectorId => {
                                    const inspector = companyInspectors.find(i => i.id === inspectorId);
                                    if (!inspector) return null;
                                    
                                    return (
                                      <span 
                                        key={inspectorId}
                                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                      >
                                        {`${inspector.first_name || ''} ${inspector.last_name || ''}`.trim() || inspector.username}
                                        <button
                                          type="button"
                                          onClick={() => handleInspectorToggle(groupKey, inspectorId)}
                                          className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      </span>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="text-xs text-gray-500">No inspectors selected</div>
                              )}
                            </div>
                            
                            {/* Search Input and Dropdown Toggle */}
                            <div 
                              className="relative"
                              ref={(el) => {
                                if (el) dropdownRefs.current[groupKey] = el;
                              }}
                            >
                              <div className="flex items-center">
                                <div className="relative flex-1">
                                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                                  <input
                                    type="text"
                                    placeholder="Search inspectors..."
                                    value={inspectorSearchTerms[groupKey] || ''}
                                    onChange={(e) => handleInspectorSearch(groupKey, e.target.value)}
                                    onFocus={() => setInspectorDropdownOpen(prev => ({ ...prev, [groupKey]: true }))}
                                    className="w-full text-sm border border-gray-300 rounded-l-md pl-7 pr-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => toggleInspectorDropdown(groupKey)}
                                  className="px-2 py-1 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <ChevronDown className={`h-3 w-3 text-gray-400 transition-transform ${inspectorDropdownOpen[groupKey] ? 'rotate-180' : ''}`} />
                                </button>
                              </div>
                              
                              {/* Dropdown List */}
                              {inspectorDropdownOpen[groupKey] && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
                                  {getFilteredInspectors(groupKey).length > 0 ? (
                                    getFilteredInspectors(groupKey).map(inspector => {
                                      const isSelected = (selectedInspectors[groupKey] || []).includes(inspector.id);
                                      return (
                                        <div
                                          key={inspector.id}
                                          onClick={() => handleInspectorToggle(groupKey, inspector.id)}
                                          className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 flex items-center justify-between ${
                                            isSelected ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                                          }`}
                                        >
                                          <div>
                                            <div className="font-medium">
                                              {`${inspector.first_name || ''} ${inspector.last_name || ''}`.trim() || inspector.username}
                                            </div>
                                            {inspector.email && (
                                              <div className="text-xs text-gray-500">{inspector.email}</div>
                                            )}
                                          </div>
                                          {isSelected && (
                                            <div className="w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center">
                                              <X className="h-2 w-2 text-white" />
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })
                                  ) : (
                                    <div className="px-3 py-2 text-sm text-gray-500">
                                      No inspectors found
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Equipment List */}
                  <div className="bg-white rounded-lg p-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Equipment to notify:</h4>
                    <div className="space-y-2">
                      {group.equipment.map((equipment, equipIndex) => (
                        <div key={equipIndex} className="space-y-2">
                          <div className="text-sm bg-gray-50 rounded p-3">
                            {/* First row: Type-Model and Validity Headers */}
                            <div className="flex justify-between items-start mb-2">
                              <div className="font-medium">
                                {equipment.safety_equipment_type_name || 'Unknown Type'}
                                {equipment.safety_equipment_model_name && (
                                  <span className="text-gray-600 ml-1">- {equipment.safety_equipment_model_name}</span>
                                )}
                              </div>
                              <div className="flex text-xs">
                                <label className="flex items-center space-x-1 cursor-pointer w-40">
                                  <input
                                    type="checkbox"
                                    checked={notificationSelections[equipment.id]?.manufacturer || false}
                                    onChange={() => handleNotificationTypeChange(equipment.id, 'manufacturer')}
                                    className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <span className="text-gray-600 font-medium">Manufacturer Validity:</span>
                                </label>
                                <label className="flex items-center space-x-1 cursor-pointer w-40">
                                  <input
                                    type="checkbox"
                                    checked={notificationSelections[equipment.id]?.inspection || false}
                                    onChange={() => handleNotificationTypeChange(equipment.id, 'inspection')}
                                    className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <span className="text-gray-600 font-medium">Inspection Validity:</span>
                                </label>
                              </div>
                            </div>
                            
                            {/* Second row: Serial Number and Validity Values */}
                            <div className="flex justify-between items-end">
                              <div>
                                {equipment.safety_equipment_serial_number && (
                                  <div className="text-gray-500 text-xs">
                                    Serial: {equipment.safety_equipment_serial_number}
                                  </div>
                                )}
                              </div>
                              <div className="flex text-xs">
                                <div className="w-40 flex justify-center">
                                  <span className={`px-2 py-1 rounded whitespace-nowrap ${
                                    getManufacturerValidityStatus(equipment) === 'expired'
                                      ? 'bg-red-100 text-red-800'
                                      : getManufacturerValidityStatus(equipment) === 'expiring'
                                      ? 'bg-orange-100 text-orange-800'
                                      : 'bg-green-100 text-green-800'
                                  }`}>
                                    {getManufacturerValidityDisplay(equipment)}
                                  </span>
                                </div>
                                <div className="w-40 flex justify-center">
                                  <span className={`px-2 py-1 rounded whitespace-nowrap ${
                                    getInspectionValidityStatus(equipment) === 'expired'
                                      ? 'bg-red-100 text-red-800'
                                      : getInspectionValidityStatus(equipment) === 'expiring'
                                      ? 'bg-orange-100 text-orange-800'
                                      : 'bg-green-100 text-green-800'
                                  }`}>
                                    {getInspectionValidityDisplay(equipment)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Invalid groups (without emails) */}
              {groupedNotifications.filter(group => !group.hasValidEmail).map((group, index) => (
                <div key={`invalid-${index}`} className="border border-red-200 bg-red-50 rounded-lg p-4">
                  {/* Recipient Info */}
                  <div className="flex items-start space-x-3 mb-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                        {group.recipient.type === 'user' ? (
                          <Users className="h-5 w-5 text-red-600" />
                        ) : group.recipient.type === 'company' ? (
                          <Building className="h-5 w-5 text-red-600" />
                        ) : (
                          <Package className="h-5 w-5 text-red-600" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-medium text-gray-900">
                          {group.recipient.name}
                        </h3>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          ✗ Cannot send
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        {group.recipient.company}
                      </p>
                      <p className="text-sm text-red-600 font-medium">
                        {group.recipient.email || 'No email configured'}
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        ⚠️ {group.recipient.type === 'company' 
                          ? 'Company email address not configured in system'
                          : group.recipient.type === 'user'
                          ? 'User does not have company email configured'
                          : 'Unable to determine recipient for this equipment'
                        }
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <Package className="h-4 w-4" />
                      <span>{group.equipment.length} item{group.equipment.length > 1 ? 's' : ''}</span>
                    </div>
                  </div>

                  {/* Equipment List */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Equipment to notify:</h4>
                    <div className="space-y-2">
                      {group.equipment.map((equipment, equipIndex) => (
                        <div key={equipIndex} className="space-y-2">
                          <div className="flex items-start justify-between text-sm bg-white rounded p-3">
                            <div className="flex-1">
                              <div className="font-medium">
                                {equipment.safety_equipment_type_name || 'Unknown Type'}
                                {equipment.safety_equipment_model_name && (
                                  <span className="text-gray-600 ml-1">- {equipment.safety_equipment_model_name}</span>
                                )}
                              </div>
                              {equipment.safety_equipment_serial_number && (
                                <div className="text-gray-500 text-xs mt-1">
                                  Serial: {equipment.safety_equipment_serial_number}
                                </div>
                              )}
                            </div>
                            <div className="text-xs min-w-0 ml-4">
                              <div className="flex flex-col items-end space-y-1">
                                <div className="flex items-center space-x-2">
                                  <span className="text-gray-600 font-medium text-right">Manufacturer:</span>
                                  <span className={`px-2 py-1 rounded text-xs whitespace-nowrap ${
                                    getManufacturerValidityStatus(equipment) === 'expired'
                                      ? 'bg-red-100 text-red-800'
                                      : getManufacturerValidityStatus(equipment) === 'expiring'
                                      ? 'bg-orange-100 text-orange-800'
                                      : 'bg-green-100 text-green-800'
                                  }`}>
                                    {getManufacturerValidityDisplay(equipment)}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-gray-600 font-medium text-right">Inspection:</span>
                                  <span className={`px-2 py-1 rounded text-xs whitespace-nowrap ${
                                    getInspectionValidityStatus(equipment) === 'expired'
                                      ? 'bg-red-100 text-red-800'
                                      : getInspectionValidityStatus(equipment) === 'expiring'
                                      ? 'bg-orange-100 text-orange-800'
                                      : 'bg-green-100 text-green-800'
                                  }`}>
                                    {getInspectionValidityDisplay(equipment)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No valid recipients found</h3>
              <p className="text-sm text-gray-600">
                None of the selected equipment items have valid email addresses for notification.
              </p>
            </div>
          )}

          {groupedNotifications.length > 0 && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 text-sm">ℹ️</span>
                  </div>
                </div>
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Notification Summary:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>{equipmentList.length} equipment items selected</li>
                    <li>{groupedNotifications.filter(g => g.hasValidEmail).length} email{groupedNotifications.filter(g => g.hasValidEmail).length > 1 ? 's' : ''} will be sent</li>
                    {groupedNotifications.filter(g => !g.hasValidEmail).length > 0 && (
                      <li className="text-orange-700">
                        {groupedNotifications.filter(g => !g.hasValidEmail).reduce((sum, g) => sum + g.equipment.length, 0)} items cannot be sent (missing email configuration)
                      </li>
                    )}
                    <li>Equipment is grouped by recipient to avoid duplicate emails</li>
                    <li>Each email will contain Polish and English content</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 sticky bottom-0 bg-white rounded-b-xl">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Cancel
          </button>
          <button
            onClick={handleSendNotifications}
            disabled={groupedNotifications.filter(g => g.hasValidEmail).length === 0 || isLoading}
            className={`px-6 py-2 text-sm font-medium border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              groupedNotifications.filter(g => g.hasValidEmail).length > 0 && !isLoading
                ? 'text-white bg-orange-600 hover:bg-orange-700 focus:ring-orange-500'
                : 'text-gray-400 bg-gray-200 cursor-not-allowed'
            }`}
          >
            {(() => {
              const validCount = groupedNotifications.filter(g => g.hasValidEmail).length;
              if (isLoading) return 'Sending...';
              if (validCount === 0) return 'No valid recipients';
              return `Send ${validCount} Notification${validCount > 1 ? 's' : ''}`;
            })()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InspectionNotification;